import {
  Component,
  Input,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { FormGroup } from '@angular/forms';

import { BaseComponentCanDeactivate } from 'app/@auth/guards/changes.guard';
import { NbDialogService } from '@nebular/theme';
import { isObservable } from 'rxjs';
import { CalculateBlockSummary } from 'app/BuildOps';
import {
  BlockType,
  FitnessBlock,
} from 'app/@core/interfaces/common/CalorieFriend/fitnessItem';
import { FitnessSummaryComponent } from '../FitnessSummary/fitness-summary.component';
import { FitnessItemsListPresentationComponent } from '../FitnessItemsListPresentation/fitness-items-list-presentation.component';
import { SmartEditComponent } from 'app/@components/smart-edit/smart-edit.component';
import { format } from 'support';
import { takeUntil } from 'rxjs/operators';

/**
 * Base Component class for Block Set Presentation.
 * Consists of Block Items List and Metrics Graph View.
 */
export class FitnessBlockComponentBase
  extends BaseComponentCanDeactivate
  implements OnInit, AfterViewInit, OnDestroy {
  get IsDirty() {
    return (
      this.isDirty ||
      (this.FitnessItemsView != null && this.FitnessItemsView.IsDirty)
    );
  }

  resetDirty() {
    super.resetDirty();
    this.FitnessItemsView.resetDirty();
  }

  @Input('FitnessBlock') FitnessBlock: FitnessBlock;
  @Input('Index') Index: number;
  @Input('PlanView') PlanView: boolean = false;

  @Input('ShowCooldown') ShowCooldown: boolean = false;
  @Input('ShowWarmup') ShowWarmup: boolean = false;

  @Input('PatientID') PatientID: number = -1;
  @Input('PatientName') PatientName: string = '';
  @Input('IsPreferredUnitImperial') IsPreferredUnitImperial: boolean = false;

  TheBlockLabel = $localize`:@@Block:Block`;

  BlockExercises = 0;
  BlockReps = 0;
  BlockMinutes = 0;

  static clipboard: FitnessBlock;

  @ViewChild('FitnessItems')
  FitnessItemsView: FitnessItemsListPresentationComponent;
  @ViewChild('FitnessSummary') Summary: FitnessSummaryComponent;
  @ViewChild('BlockNameEdit') BlockNameEdit: SmartEditComponent;
  // to-do: for repetition and reps, circuits/block dropdown

  ItemUpdateNotification: Subject<Object> = null;

  BlockProperitesForm: FormGroup;

  BlockType = BlockType;

  unsubscription$ = new Subject();

  /**
   * Function to get Meal's label.
   */
  get BlockLabel() {
    return format(
      '{0} {1} ({2} {3})',
      this.TheBlockLabel,
      (this.Index + 1).toString(),
      this.FitnessBlock.name,
      this.FitnessBlock.blockType,
    );
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    dialogService: NbDialogService,
  ) {
    super(dialogService);
    this.ItemUpdateNotification = new Subject<Object>();
  }
  
  get MealTime() {
    return this.BlockProperitesForm.get('MealTime');
  }

  /**
   * Initializes the Meal Items subscriptions.
   */
  ngOnInit(): void {
    this.PrepareFitnessItemsObservables();
    this.PrepareMetrics();
  }

  /**
   * AfterViewInit implementation.
   * Updates the metrics graph whenever the data has ben changed.
   */
  ngAfterViewInit() {
    this.FitnessItemsView?.ItemUpdateNotification
    ?.pipe(takeUntil(this.unsubscription$))
    ?.subscribe(() => {
      this.PrepareMetrics();
    });
    
    //this.cdRef.markForCheck();
    
    if (this.BlockNameEdit != null) {
      this.BlockNameEdit.CallbackThis = this;
    }
    
    // this.HTMLMealTime.defaultTime = this.PlanMeal.time;
    // this.HTMLMealTime.value = this.PlanMeal.time;
  }
  
  /**
   * Unsubscribe all subscriptions.
  */
  ngOnDestroy(): void {
    this.unsubscription$.next();
    this.unsubscription$.complete();
  }

  FitnessBlockItems() {
    return this.FitnessBlock.items.sort((a, b) => a.order - b.order);
  }

  MealTimeChange($event) {}

  /**
   * Function to prepare meal items' update subscriptions.
   */
  PrepareFitnessItemsObservables() {
    // for the meal items  -- configure an observable
    // place an observable on the object
    if (this.FitnessBlock !== null && this.FitnessBlock !== undefined) {
      if (
        this.FitnessBlock.items !== null &&
        this.FitnessBlock.items !== undefined &&
        this.FitnessBlock.items.length > 0
      ) {
        for (const item of this.FitnessBlock.items) {
          if (!isObservable(item.Update)) item.Update = new Subject();

          item.Update
            .pipe(takeUntil(this.unsubscription$))
            .subscribe((data) => this.PrepareMetrics());
        }
      }
    }
  }

  /**
   * Method to update the metrics data.
   */
  private PrepareMetrics() {
    const blockSummary = CalculateBlockSummary(this.FitnessBlock);

    this.BlockExercises = blockSummary.Exercises;
    this.BlockReps = blockSummary.TotalReps;
    this.BlockMinutes = Math.round(blockSummary.TotalSeconds / 60);

    if (this.FitnessBlock.blockType.toString() === BlockType.Cooldown.toString()) {
      this.FitnessBlock.order = 100;
    }

    if (this.FitnessBlock.blockType.toString() === BlockType.Warmup.toString()) {
      this.FitnessBlock.order = -100;
    }

    // notify Parent - FitnessDay component
    this.ItemUpdateNotification.next();
  }

  public UpdateBlockLabel(CurrentLabel: string, ThisThis) {
    ThisThis.FitnessBlock.name = CurrentLabel;

    if (
      ThisThis.FitnessBlock.blockType.toString() === BlockType.Block.toString() ||
      ThisThis.FitnessBlock.blockType.toString() === BlockType.Circuit.toString()
    )
      return format(
        '{0} {1} ({2})',
        ThisThis.TheBlockLabel,
        String.fromCharCode(64 + ThisThis.FitnessBlock.order),
        ThisThis.FitnessBlock.name,
      );

    if (ThisThis.FitnessBlock.blockType.toString() === BlockType.Cooldown.toString())
      return format('{0} ({1})', 'Cooldown', ThisThis.FitnessBlock.name);

    if (ThisThis.FitnessBlock.blockType.toString() === BlockType.Warmup.toString())
      return format('{0} ({1})', 'Warmup', ThisThis.FitnessBlock.name);

  }

  public UpdateLabel() {
    this.BlockNameEdit.UpdateSecondValue();
    this.cdRef.detectChanges();
  }

  BlockNameChange($event) {
    this.FitnessBlock.name = $event.target.value;
  }

  /**
   * Removes the selected block
   */
  RemoveBlock() {}

  /**
   * Checks if Block can be removed from the Day
   */
  get CanRemoveBlock() {
    return true;
  }
}

/**
 * Component for Block Presentation.
 * Inherits from the BlockPresentationComponentBase component.
 */
@Component({
  selector: 'app-fitness-block',
  templateUrl: './fitness-block.component.html',
  styleUrls: ['./fitness-block.component.scss', '../../global.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class FitnessBlockComponent
  extends FitnessBlockComponentBase
  implements OnInit, OnDestroy {
  constructor(
    cdRefBlock: ChangeDetectorRef,
    dialogServiceBlock: NbDialogService,
  ) {
    super(cdRefBlock, dialogServiceBlock);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }
}

/**
 * Component for Block Presentation Mobile.
 * Inherits from the BlockPresentationComponentBase component.
 */
@Component({
  selector: 'app-fitness-block-mobile',
  templateUrl: './fitness-block-mobile.component.html',
  styleUrls: ['./fitness-block.component.scss', '../../global.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class FitnessBlockMobileComponent
  extends FitnessBlockComponentBase
  implements OnInit {
  constructor(
    cdRefBlockMobile: ChangeDetectorRef,
    dialogServiceBlockMobile: NbDialogService,
  ) {
    super(cdRefBlockMobile, dialogServiceBlockMobile);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.PlanView = true;
  }

  get FitnessBlockOrderName() {
    return this.UpdateBlockLabel(this.FitnessBlock.name, this);
  }
}
